import { useEffect, useRef, useState } from 'react'

/**
 * Largura do container, medida por conta própria — para passar `width` explícito
 * a um gráfico do Recharts em vez de usar `<ResponsiveContainer>`.
 *
 * ⚠️ **O `<ResponsiveContainer>` não funciona neste projeto.** O CSS global
 * (`.recharts-wrapper { width:100% !important; height:100% !important }`, em
 * `index.css`) sobrescreve as dimensões que ele calcula, então a medição inicial de
 * zero nunca se corrige sozinha: o gráfico nasce em branco e só aparece quando o
 * usuário redimensiona a janela e o Recharts refaz as contas. O sintoma é
 * **"o gráfico não carrega no Chrome"** — o Safari mede em outro momento do layout e
 * costuma escapar, o que faz a investigação ir para o navegador em vez de ir para o
 * CSS.
 *
 * Pelo mesmo motivo, não use `<Legend>`: `.recharts-legend-wrapper { position:
 * relative !important }` colapsa o wrapper do gráfico para largura zero. Desenhe a
 * legenda em HTML.
 */
export function useContainerWidth() {
    const ref = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState(0)

    useEffect(() => {
        const element = ref.current
        if (!element) return

        const measure = () => setWidth(element.clientWidth)
        measure()

        const observer = new ResizeObserver(measure)
        observer.observe(element)
        // O primeiro paint pode acontecer antes de o layout estabilizar (card
        // recém-montado, aba em segundo plano): remede no frame seguinte.
        const frame = requestAnimationFrame(measure)

        return () => {
            observer.disconnect()
            cancelAnimationFrame(frame)
        }
    }, [])

    return { ref, width }
}
